import React, { useContext, useCallback } from 'react';
import { cloneDeep } from 'lodash';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import { Typography } from '@material-ui/core';

import { CheckboxField as Checkbox } from 'components/checkbox-field/checkbox-field';
import { TooltipWrapper } from 'components/tooltip/tooltip-wrapper';
import { ArchivalAidMetadata } from 'types';
import { ApiRoute } from 'enums';
import { DialogContext } from 'components/dialog/dialog-context';
import { StructMapQuoteDialog } from 'components/dialog/aisuk-dialogs/aisuk-struct-map-quote-dialog';
import { getArchivalUnitQuote } from 'modules/archival-unit/utils/get-archival-unit-quote';
import useFetch from 'utils/use-fetch';
import { isImageFromContentType } from 'utils/is-image';

import { StructMapCtx } from './struct-map-ctx';
import { StructMapItemProps } from './struct-map-types';
import { getAllFiles, removeNodes } from './struct-map-utils';
import { getFileUrl } from './utils';
import { findValidDescribingAidId } from 'components/grid/v2/context/hierarchy-context';

export function StructMapItemLeaf({ label, data, parent }: StructMapItemProps) {
  const {
    allowDrag,
    allowDelete,
    allowSelect,
    allowSpecialSelect,
    allData,
    classes,
    leafTooltipMapper,
    LeafActions,
    selectedNodes,
    setSelectedNodes,
    specialSelectedNodes,
    setSpecialSelectedNodes,
    draggedNodes,
    setDraggedNodes,
    onChange,
    onLeafClick,
    missingNodes,
    extraNodes,
    parentEntity,
  } = useContext(StructMapCtx);

  const { open } = useContext(DialogContext);

  /**
   * TRUE if current leaf is selected.
   */
  const found = selectedNodes.find((node) => node === data.id);

  /**
   * TRUE if current leaf is special selected.
   */
  const specialFound = specialSelectedNodes.find((node) => node === data.id);

  /**
   * Flag that indicates missing item in new version of struct map.
   */
  const isMissing = missingNodes.some((missingId) => missingId === data.id);

  /**
   * Flag that indicates extra item in new version of struct map.
   */
  const isExtra = extraNodes.some((extraId) => extraId === data.id);

  /**
   * Flag if the file is an image
   */
  const isImage = isImageFromContentType(data.fileRef?.contentType);

  /**
   * Toggle select leaf.
   */
  const toggleSelected = useCallback(() => {
    if (!!found) {
      setSelectedNodes((nodes) => [
        ...nodes.filter((node) => node !== data.id),
      ]);
    } else {
      setSelectedNodes((nodes) => [...nodes, data.id!]);
    }
  }, [data.id, found, setSelectedNodes]);

  /**
   * Toggle special select leaf.
   */
  const toggleSpecialSelected = useCallback(() => {
    if (!!specialFound) {
      setSpecialSelectedNodes((nodes) => [
        ...nodes.filter((node) => node !== data.id),
      ]);
    } else {
      setSpecialSelectedNodes((nodes) => [...nodes, data.id!]);
    }
  }, [data.id, setSpecialSelectedNodes, specialFound]);

  const tooltip =
    !draggedNodes.length && leafTooltipMapper
      ? leafTooltipMapper(data)
      : undefined;

  /**
   *
   */
  const handleDragStart = useCallback(() => {
    if (selectedNodes.length !== 0) {
      const draggedFiles = getAllFiles(allData).filter((file) =>
        selectedNodes.some((sid) => sid === file.id)
      );

      setDraggedNodes(draggedFiles);
    } else {
      setDraggedNodes([data]);
    }
  }, [allData, data, selectedNodes, setDraggedNodes]);

  /**
   *
   */
  const handleDrop = useCallback(() => {
    setDraggedNodes([]);
  }, [setDraggedNodes]);

  const handleDelete = useCallback(() => {
    const newData = removeNodes(cloneDeep(allData), [data.id!]);

    onChange(newData);
  }, [allData, data.id, onChange]);

  const handleClick = useCallback(() => {
    onLeafClick?.({ node: data, parent });
  }, [data, onLeafClick, parent]);

  const fileUrl = getFileUrl(data.fileRef?.id);

  const aidId = findValidDescribingAidId(
    parentEntity.archivalUnitReference?.describingArchivalAids
  );
  const { data: archivalAid } = useFetch<ArchivalAidMetadata>(
    aidId ? `/api/${ApiRoute.ARCHIVAL_AID}/${aidId}` : ''
  );

  const handleOpenQuoteDialog = useCallback(() => {
    open({
      initialValues: {
        quoteContent: getArchivalUnitQuote(
          parentEntity.archivalUnitReference,
          archivalAid,
          parentEntity.summary,
          data.subDivisions ? data.aisId : data.label
        ),
      },
      size: 'md',
      Content: StructMapQuoteDialog,
    });
  }, [
    archivalAid,
    data.aisId,
    data.label,
    data.subDivisions,
    open,
    parentEntity.archivalUnitReference,
    parentEntity.summary,
  ]);

  return (
    <div
      className={classes.item}
      draggable={allowDrag}
      onDragStart={handleDragStart}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <TooltipWrapper tooltip={tooltip}>
        {isImage ? (
          <Typography
            className={classNames(classes.label, {
              [classes.missingItem]: isMissing,
              [classes.extraItem]: isExtra,
            })}
          >
            {label}
          </Typography>
        ) : (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames(classes.label, {
              [classes.missingItem]: isMissing,
              [classes.extraItem]: isExtra,
            })}
          >
            {label}
          </a>
        )}
      </TooltipWrapper>
      <div onClick={(e) => e.stopPropagation()} className={classes.actions}>
        <LeafActions leaf={data} classes={classes} />
        {allowSelect && (
          <Checkbox
            name="found"
            value={!!found}
            onChange={toggleSelected}
            disabled={false}
          />
        )}
        {allowSpecialSelect && (
          <Checkbox
            name="specialFound"
            value={!!specialFound}
            onChange={toggleSpecialSelected}
            disabled={false}
          />
        )}
        {allowDelete && (
          <IconButton
            classes={{ root: classes.crudButton }}
            onClick={handleDelete}
          >
            <DeleteIcon className={classes.crudIcon} />
          </IconButton>
        )}
        <Button
          onClick={handleOpenQuoteDialog}
          size="small"
          className={classes.downloadButton}
        >
          <FormatQuoteIcon />
        </Button>
      </div>
    </div>
  );
}
